import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import { Label } from '@/design_system/Label/Label';
import { RadioBox, RadioBoxGroup } from '@/design_system/Radio/RadioBox/RadioBox';
import InlineText from '@/design_system/Typography/InlineText';
import IconExternalLink from '@/icons/ExternalLink.svg';
import { useClientShippingOptions } from '@/models/client';
import { RequestWithRelations, useUpdateRequestClient } from '@/models/request';
import { SHIPMENT_CARRIERS } from '@/models/shipment';
import { formatCurrency } from '@/utils/number';

import { DropOffStoresDialog } from '../../DropOffStoresDialog';

export const ClientShippingOption = ({ request }: { request: RequestWithRelations }) => {
  const { t } = useLingui();

  const [shippingOption, setShippingOption] = useState<'home' | 'store' | null>(
    request.client?.shippingChoice ?? null
  );
  const [isOpenStoreListModal, setIsOpenStoreListModal] = useState(false);

  const { data: shippingOptions = [] } = useClientShippingOptions({
    requestId: request.id,
    enabled: request.articles.length > 0,
  });

  const { mutateAsync: updateRequestClient } = useUpdateRequestClient(request.id);

  const onChange = async (value: 'home' | 'store') => {
    setShippingOption(value);

    await updateRequestClient({
      client: {
        shippingChoice: value,
      },
    }).catch(() => {
      setShippingOption(null);
    });
  };

  const options = [];

  const storeOption = shippingOptions.find((option) => option.type === 'store');
  const homeOption = shippingOptions.find((option) => option.type === 'home');

  if (storeOption) {
    options.push(
      <RadioBox
        key="store"
        value="store"
        ariaLabel={t({
          id: 'client.articles.shipping-option.store',
          message: 'Bring it to a store',
        })}
        title={<Trans id="client.articles.shipping-option.store">Bring it to a store</Trans>}
        info={
          storeOption.fee.amount > 0 ? (
            formatCurrency(storeOption.fee.amount, storeOption.fee.currency)
          ) : (
            <Trans id="client.articles.shipping-option.free">Free</Trans>
          )
        }
        actions={
          <Button variant="link" onPress={() => setIsOpenStoreListModal(true)}>
            <Trans id="client.articles.shipping-option.store.action">See authorized stores</Trans>
          </Button>
        }
      >
        <InlineText>
          <Trans id="client.articles.shipping-option.store.description">
            Drop your item at the nearest authorized {request.organization.name} store and let them
            handle the shipment.
          </Trans>
        </InlineText>
      </RadioBox>
    );
  }

  if (homeOption) {
    const carrier = SHIPMENT_CARRIERS.find(({ id }) => id === homeOption.carrier.name);

    options.push(
      <RadioBox
        key="home"
        value="home"
        ariaLabel={t({
          id: 'client.articles.shipping-option.home',
          message: 'Via a carrier',
        })}
        title={<Trans id="client.articles.shipping-option.home">Via a carrier</Trans>}
        info={
          homeOption.fee.amount > 0 ? (
            formatCurrency(homeOption.fee.amount, homeOption.fee.currency)
          ) : (
            <Trans id="client.articles.shipping-option.free">Free</Trans>
          )
        }
        actions={
          !!carrier &&
          !!carrier.dropoffPoints && (
            <div>
              <Button variant="link" href={carrier.dropoffPoints} target="_blank">
                <Trans id="client.articles.shipping-option.home.action">View drop-off points</Trans>
                <IconExternalLink />
              </Button>
            </div>
          )
        }
      >
        <InlineText>
          <Trans id="client.articles.shipping-option.home.description">
            Print and stick the shipment label we'll provide you on the package. Then go to a
            carrier drop-off point to send it.
          </Trans>
        </InlineText>
      </RadioBox>
    );
  }

  if (options.length === 0) {
    return null;
  }

  return (
    <Label
      label={t({
        id: 'client.articles.shipping-option.label',
        message: 'Choose your shipping option',
      })}
    >
      <RadioBoxGroup
        name="shipping-option"
        theme="brand"
        value={shippingOption}
        onChange={onChange}
      >
        {options}
      </RadioBoxGroup>
      <DropOffStoresDialog isOpen={isOpenStoreListModal} onOpenChange={setIsOpenStoreListModal} />
    </Label>
  );
};
